import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Hero from "./components/Hero";
import CategoryDropdown from "./components/CategoryDropdown";
import CategoryGrid from "./components/CategoryGrid";
import ArticleGrid from "./components/ArticleGrid";
import FaqSection from "./components/FaqSection";
import { mainSiteUrl } from "@/data/categories";

const aboutCards = [
  {
    key: "categories",
    href: "#categories",
    icon: "ri-apps-2-line",
    textKey: "nav.categories",
    image:
      "https://readdy.ai/api/search-image?query=A%20tidy%20overhead%20flat%20lay%20of%20colorful%20folded%20clothing%20items%20and%20small%20fashion%20products%20arranged%20in%20neat%20labeled%20groups%20on%20a%20soft%20warm%20cream%20background%2C%20minimal%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=400&height=300&seq=about-categories&orientation=landscape",
  },
  {
    key: "guides",
    href: "#guides",
    icon: "ri-book-open-line",
    textKey: "nav.guides",
    image:
      "https://readdy.ai/api/search-image?query=An%20open%20notebook%20with%20a%20pen%20and%20a%20small%20neat%20stack%20of%20guide%20booklets%20on%20a%20warm%20wooden%20desk%20with%20a%20soft%20sage%20green%20accent%20cloth%2C%20natural%20light%2C%20minimal%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=400&height=300&seq=about-guides&orientation=landscape",
  },
  {
    key: "faq",
    href: "#faq",
    icon: "ri-question-line",
    textKey: "nav.faq",
    image:
      "https://readdy.ai/api/search-image?query=A%20minimal%20desk%20scene%20with%20a%20coffee%20cup%20and%20a%20small%20wooden%20sign%20with%20a%20question%20mark%20shape%20beside%20a%20tablet%20on%20a%20warm%20cream%20background%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=400&height=300&seq=about-faq&orientation=landscape",
  },
];

export default function Home() {
  const { t } = useTranslation();

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: t("faq.q1"),
        acceptedAnswer: { "@type": "Answer", text: t("faq.a1") },
      },
      {
        "@type": "Question",
        name: t("faq.q2"),
        acceptedAnswer: { "@type": "Answer", text: t("faq.a2") },
      },
      {
        "@type": "Question",
        name: t("faq.q3"),
        acceptedAnswer: { "@type": "Answer", text: t("faq.a3") },
      },
      {
        "@type": "Question",
        name: t("faq.q4"),
        acceptedAnswer: { "@type": "Answer", text: t("faq.a4") },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Navbar />
      <main>
        <Hero />

        {/* About / text module */}
        <section id="about" className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-secondary-200 bg-secondary-50 px-4 py-1.5 text-sm font-medium text-secondary-900">
                <i className="ri-information-line"></i>
                {t("hero.eyebrow")}
              </span>
              <h2 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t("about.title")}</h2>
              <div className="mt-5 flex flex-col gap-4">
                <p className="text-foreground-600 leading-relaxed">{t("about.p1")}</p>
                <p className="text-foreground-600 leading-relaxed">{t("about.p2")}</p>
                <p className="text-foreground-600 leading-relaxed">{t("about.p3")}</p>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20bright%20airy%20flat%20lay%20of%20a%20laptop%20showing%20a%20colorful%20spreadsheet%20grid%20surrounded%20by%20a%20folded%20sage%20green%20hoodie%2C%20a%20pair%20of%20white%20sneakers%20and%20a%20small%20canvas%20shopping%20bag%20on%20a%20warm%20cream%20desk%2C%20soft%20natural%20light%2C%20fresh%20minimal%20editorial%20photography%2C%20high%20detail%2C%20no%20text&width=800&height=500&seq=about-main&orientation=landscape&nocache=true"
                  alt={t("about.title")}
                  title="Basetao Spreadsheet"
                  className="h-56 w-full object-cover object-top"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {aboutCards.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition hover:-translate-y-0.5 cursor-pointer"
                  >
                    <div className="relative h-24 w-full overflow-hidden">
                      <img
                        src={item.image}
                        alt={t(item.textKey)}
                        title={`${t(item.textKey)} - Basetao Spreadsheet`}
                        className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2 px-3 py-3">
                      <span className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                          <i className={`${item.icon} text-sm`}></i>
                        </span>
                        <span className="whitespace-nowrap">{t(item.textKey)}</span>
                      </span>
                      <i className="ri-arrow-down-line text-foreground-400"></i>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Category dropdown */}
        <section className="mx-auto w-full max-w-3xl px-4 py-8 md:px-6">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
            <div className="mb-4 flex items-center gap-2">
              <i className="ri-apps-2-line text-primary-600"></i>
              <h3 className="font-heading text-lg font-semibold text-foreground-950">{t("dropdown.label")}</h3>
            </div>
            <CategoryDropdown />
          </div>
        </section>

        {/* Category grid */}
        <section id="categories" className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t("categories.title")}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-foreground-600">{t("categories.subtitle")}</p>
          </div>
          <div className="mt-10">
            <CategoryGrid />
          </div>
        </section>

        {/* Guides / articles grid */}
        <section id="guides" className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t("guides.title")}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-foreground-600">{t("guides.subtitle")}</p>
          </div>
          <div className="mt-10">
            <ArticleGrid />
          </div>
        </section>

        <FaqSection />

        {/* CTA */}
        <section className="mx-auto w-full max-w-5xl px-4 pb-4 md:px-6">
          <div className="overflow-hidden rounded-lg border border-primary-200/70 bg-primary-50 px-6 py-14 text-center md:px-12">
            <h2 className="mx-auto max-w-2xl font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t("cta.title")}</h2>
            <p className="mx-auto mt-3 max-w-xl text-foreground-600">{t("cta.subtitle")}</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-7 py-3.5 text-base font-semibold text-background-50 transition hover:bg-primary-600 cursor-pointer whitespace-nowrap sm:w-auto"
              >
                {t("cta.primary")}
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link
                to="/article/basetao-spreadsheet-guide"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-primary-300 bg-background-50 px-7 py-3.5 text-base font-semibold text-primary-700 transition hover:bg-primary-100 cursor-pointer whitespace-nowrap sm:w-auto"
              >
                {t("guides.readArticle")}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}